import React, { useState } from 'react';
import { Dialog } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

interface TestTradeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  apiKey: string;
}

interface TradeParams {
  user: string;
  position: 'long' | 'short' | 'close_long' | 'close_short';
  prod: 'true' | 'false';
  apiKey: string;
  symbol: string;
  amount: string;
  leverage: string;
  takeProfitRatio: string;
  stopLossRatio: string;
  useProxy: 'true' | 'false';
  usePostOnlyOrders: 'true' | 'false';
  marketPriceOverride?: string;
  useTrailingStop: 'true' | 'false';
  trailingStopActivationRatio?: string;
  trailingStopRatio?: string;
  tpOrderType: 'market' | 'limit' | 'mark';
  slOrderType: 'market' | 'limit' | 'mark';
  tpslPriceThreshold?: string;
  autoCancelMinutes?: string;
}

export const TestTradeDialog: React.FC<TestTradeDialogProps> = ({ 
  isOpen, 
  onClose, 
  userId, 
  apiKey 
}) => {
  const [params, setParams] = useState<TradeParams>({
    user: userId,
    position: 'long',
    prod: 'false',
    apiKey: apiKey,
    symbol: 'BTCUSDT',
    amount: '0.001',
    leverage: '10',
    takeProfitRatio: '1.02',
    stopLossRatio: '0.98',
    useProxy: 'false',
    usePostOnlyOrders: 'false',
    marketPriceOverride: '',
    useTrailingStop: 'false',
    trailingStopActivationRatio: '',
    trailingStopRatio: '',
    tpOrderType: 'limit',
    slOrderType: 'mark',
    tpslPriceThreshold: '0.0001',
    autoCancelMinutes: '30'
  });
  
  const [dataFormat, setDataFormat] = useState<'query' | 'body'>('query');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string; data?: Record<string, unknown> } | null>(null);

  const apiUrl = import.meta.env.VITE_API_URL || '';
  const tradeEndpoint = apiUrl === '' ? '' : `${apiUrl.replace(/\/+$/, '')}/trade`;

  const shouldIncludeField = (key: keyof TradeParams, value: string | undefined) => {
    if (value === undefined || value === '') {
      return false;
    }

    const isClosePosition = params.position.startsWith('close_');
    const isLimitLike = (orderType: string | undefined) => orderType === 'limit' || orderType === 'mark';

    if (key === 'marketPriceOverride') {
      return params.prod === 'false' && !isClosePosition;
    }

    if (key === 'trailingStopActivationRatio' || key === 'trailingStopRatio') {
      return params.useTrailingStop === 'true' && !isClosePosition;
    }

    if (key === 'tpOrderType' || key === 'slOrderType') {
      return !isClosePosition;
    }

    if (key === 'tpslPriceThreshold') {
      if (isClosePosition) {
        return false;
      }
      return isLimitLike(params.tpOrderType) || isLimitLike(params.slOrderType);
    }

    if (key === 'autoCancelMinutes') {
      if (isClosePosition) {
        return false;
      }
      return params.prod === 'true';
    }

    return true;
  };

  const buildFilteredParams = () => (
    Object.entries(params).reduce<Record<string, string>>((acc, [key, value]) => {
      if (typeof value === 'string' && shouldIncludeField(key as keyof TradeParams, value)) {
        acc[key] = value;
      }
      return acc;
    }, {})
  );

  const showTpslControls = !params.position.startsWith('close_');
  const showTpslThreshold = showTpslControls && (
    params.tpOrderType === 'limit' ||
    params.tpOrderType === 'mark' ||
    params.slOrderType === 'limit' ||
    params.slOrderType === 'mark'
  );

  const handleTest = async () => {
    if (!tradeEndpoint) {
      setResult({ success: false, message: 'API URL not configured' });
      return;
    }

    setIsLoading(true);
    setResult(null);

    try {
      let url = tradeEndpoint;
      const headers: HeadersInit = {
        'Accept': 'application/json',
      };
      let body: string | undefined;

      if (dataFormat === 'query') {
        const filteredParams = buildFilteredParams();
        const queryParams = new URLSearchParams(filteredParams);
        url = `${tradeEndpoint}?${queryParams.toString()}`;
      } else {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify(buildFilteredParams());
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body,
      });

      const responseText = await response.text();
      let parsedBody: Record<string, unknown> | null = null;
      try {
        parsedBody = responseText ? JSON.parse(responseText) : null;
      } catch (parseError) {
        console.warn('Unable to parse trade API response JSON', parseError);
      }
      
      if (response.ok) {
        const correlationText = parsedBody?.correlationId
          ? `Correlation ID: ${parsedBody.correlationId}`
          : 'Workflow dispatched.';
        const executionArnText = parsedBody?.executionArn ? ` Execution ARN: ${parsedBody.executionArn}` : '';

        setResult({ 
          success: true, 
          message: `Success (${response.status}): ${correlationText}${executionArnText}`,
          data: parsedBody ?? undefined,
        });
      } else {
        setResult({ 
          success: false, 
          message: `Error (${response.status}): ${responseText || 'Trade execution failed'}`,
          data: parsedBody ?? undefined,
        });
      }
    } catch (error) {
      setResult({ 
        success: false, 
        message: `Network error: ${error instanceof Error ? error.message : 'Unknown error'}` 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof TradeParams, value: string) => {
    setParams(prev => ({ ...prev, [field]: value }));
  };

  const filteredParamsForPreview = buildFilteredParams();
  const previewQueryString = new URLSearchParams(filteredParamsForPreview).toString();
  const previewUrl = previewQueryString ? `${tradeEndpoint}?${previewQueryString}` : tradeEndpoint;
  const previewBody = JSON.stringify(filteredParamsForPreview, null, 2);
  const correlationId = typeof result?.data?.correlationId === 'string' ? result.data.correlationId : undefined;
  const executionArn = typeof result?.data?.executionArn === 'string' ? result.data.executionArn : undefined;

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Test Trade API">
      <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="user">User ID</Label>
              <Input
                id="user"
                value={params.user}
                onChange={(e) => handleChange('user', e.target.value)}
                disabled
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="position">Position</Label>
              <select
                id="position"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={params.position}
                onChange={(e) => handleChange('position', e.target.value)}
              >
                <option value="long">Long</option>
                <option value="short">Short</option>
                <option value="close_long">Close Long</option>
                <option value="close_short">Close Short</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="apiKey">Trading Webhook Testnet API Key</Label>
              <Input
                id="apiKey"
                value={params.apiKey}
                onChange={(e) => handleChange('apiKey', e.target.value)}
                disabled
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="prod">Environment</Label>
              <select
                id="prod"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={params.prod}
                onChange={(e) => handleChange('prod', e.target.value)}
              >
                <option value="false">Testnet</option>
                <option value="true">Production</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="symbol">Symbol</Label>
              <select
                id="symbol"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={params.symbol}
                onChange={(e) => handleChange('symbol', e.target.value)}
              >
                <option value="BTCUSDT">BTCUSDT</option>
                <option value="ETHUSDT">ETHUSDT</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="amount">Amount</Label>
              <Input
                id="amount"
                type="number"
                step="0.001"
                value={params.amount}
                onChange={(e) => handleChange('amount', e.target.value)}
              />
            </div>
            
            {!params.position.startsWith('close_') && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="leverage">Leverage</Label>
                  <Input
                    id="leverage"
                    type="number"
                    value={params.leverage}
                    onChange={(e) => handleChange('leverage', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="takeProfitRatio">Take Profit Ratio</Label>
                  <Input
                    id="takeProfitRatio"
                    type="number"
                    step="0.01"
                    value={params.takeProfitRatio}
                    onChange={(e) => handleChange('takeProfitRatio', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="stopLossRatio">Stop Loss Ratio</Label>
                  <Input
                    id="stopLossRatio"
                    type="number"
                    step="0.01"
                    value={params.stopLossRatio}
                    onChange={(e) => handleChange('stopLossRatio', e.target.value)}
                  />
                </div>
              </>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="useProxy">Use Proxy</Label>
              <select
                id="useProxy"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={params.useProxy}
                onChange={(e) => handleChange('useProxy', e.target.value)}
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
            
            {!params.position.startsWith('close_') && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="usePostOnlyOrders">Use POST-only Orders</Label>
                  <select
                    id="usePostOnlyOrders"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={params.usePostOnlyOrders}
                    onChange={(e) => handleChange('usePostOnlyOrders', e.target.value)}
                  >
                    <option value="false">No (Market Orders)</option>
                    <option value="true">Yes (POST-only with fallback)</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="useTrailingStop">Use Trailing Stop</Label>
                  <select
                    id="useTrailingStop"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={params.useTrailingStop}
                    onChange={(e) => handleChange('useTrailingStop', e.target.value)}
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
              </>
            )}

            {params.useTrailingStop === 'true' && !params.position.startsWith('close_') && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="trailingStopActivationRatio"
                         title={params.position === 'long' 
                           ? "Ratio above entry price where trailing stop activates (e.g., 1.005 = 0.5% above)"
                           : "For shorts: Use same format as long (e.g., 1.005). System auto-converts to activate 0.5% below entry"}
                  >
                    Trailing Stop Activation Ratio
                  </Label>
                  <Input
                    id="trailingStopActivationRatio"
                    type="number"
                    step="0.001"
                    placeholder="e.g., 1.005"
                    value={params.trailingStopActivationRatio || ''}
                    onChange={(e) => handleChange('trailingStopActivationRatio', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="trailingStopRatio"
                         title={params.position === 'long'
                           ? "Ratio for distance calculation (e.g., 0.990 = trails 1% of activation price below highest). Must be between 0 and 1."
                           : "Ratio for distance calculation (e.g., 0.990 = trails 1% of activation price above lowest). Must be between 0 and 1."}
                  >
                    Trailing Stop Distance Ratio
                  </Label>
                  <Input
                    id="trailingStopRatio"
                    type="number"
                    step="0.001"
                    placeholder="e.g., 0.990 (for 1% trailing)"
                    value={params.trailingStopRatio || ''}
                    onChange={(e) => handleChange('trailingStopRatio', e.target.value)}
                  />
                </div>
              </>
            )}
            
            {showTpslControls && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="tpOrderType">TP Order Type</Label>
                  <select
                    id="tpOrderType"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={params.tpOrderType}
                    onChange={(e) => handleChange('tpOrderType', e.target.value)}
                  >
                    <option value="limit">Limit (default)</option>
                    <option value="mark">Mark Price (limit)</option>
                    <option value="market">Market</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="slOrderType">SL Order Type</Label>
                  <select
                    id="slOrderType"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={params.slOrderType}
                    onChange={(e) => handleChange('slOrderType', e.target.value)}
                  >
                    <option value="mark">Mark Price (default)</option>
                    <option value="limit">Limit</option>
                    <option value="market">Market</option>
                  </select>
                </div>

                {showTpslThreshold && (
                  <div className="space-y-2">
                    <Label htmlFor="tpslPriceThreshold"
                           title="Price threshold for TP/SL limit orders as a decimal (e.g., 0.0001 = 0.01%)"
                    >
                      TP/SL Price Threshold
                    </Label>
                    <Input
                      id="tpslPriceThreshold"
                      type="number"
                      step="0.0001"
                      placeholder="e.g., 0.0001 (0.01%)"
                      value={params.tpslPriceThreshold || ''}
                      onChange={(e) => handleChange('tpslPriceThreshold', e.target.value)}
                    />
                  </div>
                )}
              </>
            )}

            {params.prod === 'true' && !params.position.startsWith('close_') && (
              <div className="space-y-2">
                <Label htmlFor="autoCancelMinutes"
                       title="Automatically cancel the entry order if it has not filled within this many minutes">
                  Auto-Cancel Minutes
                </Label>
                <Input
                  id="autoCancelMinutes"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="30"
                  value={params.autoCancelMinutes || ''}
                  onChange={(e) => handleChange('autoCancelMinutes', e.target.value)}
                />
              </div>
            )}

            {params.prod === 'false' && (
              <div className="space-y-2">
                <Label htmlFor="marketPriceOverride">Market Price Override (Testnet Only)</Label>
                <Input
                  id="marketPriceOverride"
                  type="number"
                  step="0.01"
                  placeholder="Optional - leave empty to use real price"
                  value={params.marketPriceOverride || ''}
                  onChange={(e) => handleChange('marketPriceOverride', e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Data Format</Label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="dataFormat"
                  value="query"
                  checked={dataFormat === 'query'}
                  onChange={() => setDataFormat('query')}
                  className="mr-2"
                />
                Query String
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="dataFormat"
                  value="body"
                  checked={dataFormat === 'body'}
                  onChange={() => setDataFormat('body')}
                  className="mr-2"
                />
                Message Body
              </label>
            </div>
          </div>

          {tradeEndpoint && (
            <div className="bg-gray-50 p-3 rounded text-xs">
              <p className="font-semibold mb-1">Request Preview:</p>
              <div className="bg-gray-100 p-2 rounded space-y-1">
                <div><span className="font-semibold">Method:</span> POST</div>
                <div>
                  <span className="font-semibold">Webhook URL:</span>{' '}
                  <code className="break-all">
                    {dataFormat === 'query' ? previewUrl : tradeEndpoint}
                  </code>
                </div>
                {dataFormat === 'body' && (
                  <div>
                    <span className="font-semibold">Message Body:</span>
                    <pre className="mt-1 bg-gray-200 p-2 rounded overflow-x-auto">
{previewBody}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}

          {result && (
            <div className={`p-4 rounded-md ${result.success ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className="flex items-start">
                {result.success ? (
                  <CheckCircle2 className="h-5 w-5 text-green-400 mt-0.5" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-400 mt-0.5" />
                )}
                <div className="ml-3 flex-1">
                  <h3 className={`text-sm font-medium ${result.success ? 'text-green-800' : 'text-red-800'}`}>
                    {result.success ? 'Success' : 'Error'}
                  </h3>
                  <div className={`mt-2 text-sm ${result.success ? 'text-green-700' : 'text-red-700'}`}>
                    <pre className="whitespace-pre-wrap break-words">{result.message}</pre>
                  </div>
                  {correlationId && (
                    <div className="mt-2 text-xs text-gray-600 space-y-1">
                      <div>
                        Correlation ID:{' '}
                        <code className="break-all">{correlationId}</code>
                      </div>
                      {executionArn && (
                        <div>
                          Execution ARN:{' '}
                          <code className="break-all">{executionArn}</code>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            <Button onClick={handleTest} disabled={isLoading || !tradeEndpoint}>
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Testing...
                </>
              ) : (
                'Test Trade'
              )}
            </Button>
          </div>
        </div>
    </Dialog>
  );
};
